// @ts-check
// Editores inline por tipo de campo (doc 09, 13.1; doc 10, 4.3 e 4.4).
// Teclas: Enter confirma (e desce), Tab e Shift+Tab confirmam e andam na linha,
// Escape confirma e fecha (como na tabela do Twenty), clique fora confirma.
import { ArrayFieldInput } from './array-field-input.js';
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  FIELD_TYPE,
  getEmptyFieldValue,
  getLabelIdentifierField,
  getRecordLabel,
  getRelationIdFieldName,
  getSortedOptions,
  isManyToOneRelation,
} from '../../lib/field-metadata.js';
import { fromAmountMicros, toAmountMicros } from '../../lib/format.js';
import { normalizeSearchText } from '../../lib/utils.js';
import { useClickOutside } from '../../ui/hooks.js';
import {
  DropdownMenuItems,
  DropdownMenuSearchInput,
  DropdownMenuSeparator,
  MenuItem,
  useKeyboardSelection,
} from '../../ui/dropdown.js';
import { Tag } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { RecordPickerList } from './record-picker.js';
import { PhoneNumberControl } from './phone-input.js';
import { openCreateRecordPanel } from '../side-panel/side-panel-state.js';
import { buildNestedPrefill } from '../side-panel/create-record-panel.js';
import { getRelationPreset } from '../../lib/relation-presets.js';
import { findPhoneCountry, getPhoneCountry, toPhoneDigits, toPhonesValue } from '../../lib/phone.js';
import { getDefaultCurrencyCode, getDefaultPhoneCountryCode } from '../../../../shared/field-types/field-settings.js';
import { isUuid } from '../../../../shared/field-types/normalize-field-value.js';
import { objectsByNameSingular, objectsState } from '../../data/metadata-store.js';
import { readLoadedRecord, updateRecordOptimistically } from '../../data/record-store.js';
import { scopeStageOptions } from '../../data/stage-scope.js';
import {
  DEFAULT_ADDRESS_COUNTRY, formatPostcodeForStorage, fromCountryLabel, readPostcodeDigits, toCountryLabel,
} from '../../../../shared/address/postcode-autofill.js';
import {
  buildAddressPartsPatch, buildSubFieldClearPatch, buildSubFieldPatch, formatAddressPreview, isPostcodeProjection, isSubFieldProjection, planPostcodeEdit,
  postcodeDigitsOf, postcodeInputAction, readSubFieldValue,
} from '../../lib/address-sub-fields.js';
import { lookupPostcode } from '../../data/navigation.js';
import { openRecord } from '../side-panel/open-record.js';
import { usePostcodeLookup } from './postcode-address-control.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../../../../shared/address/postcode-autofill.js').PostcodeAddress} PostcodeAddress
 * @typedef {'down' | 'next' | 'previous' | null} EditNavigation
 * @typedef {{ apiPatch: Record<string, unknown>, localPatch: Record<string, unknown> } | null} FieldPatch
 * @typedef {{ field: FieldMetadata, record: Record<string, any>, onCommit: (patch: FieldPatch, navigation: EditNavigation) => void,
 *   onCancel: () => void, initialCharacter?: string | null, emptyOptionLabel?: string | null,
 *   createPriorityFieldNames?: string[] }} FieldInputProps
 *   createPriorityFieldNames: em relações, campos que o formulário de "Criar" mostra logo após o nome
 */

/** Campos que alternam no clique, sem editor separado (isFieldInputOnly do Twenty). */
export const INPUT_ONLY_FIELD_TYPES = new Set([FIELD_TYPE.BOOLEAN, FIELD_TYPE.RATING]);

/**
 * @param {FieldMetadata} field
 */
export function isFieldEditableInline(field) {
  if (field.type === FIELD_TYPE.RELATION) return isManyToOneRelation(field);
  return [
    FIELD_TYPE.TEXT, FIELD_TYPE.NUMBER, FIELD_TYPE.NUMERIC, FIELD_TYPE.CURRENCY, FIELD_TYPE.DATE, FIELD_TYPE.DATE_TIME,
    FIELD_TYPE.SELECT, FIELD_TYPE.MULTI_SELECT, FIELD_TYPE.FULL_NAME, FIELD_TYPE.EMAILS, FIELD_TYPE.PHONES,
    FIELD_TYPE.LINKS, FIELD_TYPE.ADDRESS, FIELD_TYPE.RICH_TEXT, FIELD_TYPE.BOOLEAN, FIELD_TYPE.RATING, FIELD_TYPE.UUID, FIELD_TYPE.ARRAY,
  ].includes(field.type);
}

/**
 * @param {FieldMetadata} field
 * @param {unknown} value
 * @returns {FieldPatch}
 */
function simplePatch(field, value) {
  return { apiPatch: { [field.name]: value }, localPatch: { [field.name]: value } };
}

/**
 * @param {KeyboardEvent} event
 * @returns {EditNavigation | 'commit' | undefined}
 */
function getKeyAction(event) {
  if (event.key === 'Enter' && !event.shiftKey) return 'down';
  if (event.key === 'Tab') return event.shiftKey ? 'previous' : 'next';
  if (event.key === 'Escape') return 'commit';
  return undefined;
}

/**
 * Contêiner do editor flutuante sobreposto à célula.
 * @param {{ children: any, onOutside: () => void, wide?: boolean, className?: string }} props
 */
function EditorSurface({ children, onOutside, wide = false, className = '' }) {
  const surfaceRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  useClickOutside(surfaceRef, onOutside);
  return html`<div
    ref=${surfaceRef}
    class=${`field-input-surface ${className}`}
    data-wide=${wide ? 'true' : undefined}
    onClick=${(event) => event.stopPropagation()}
    onMouseDown=${(event) => event.stopPropagation()}
  >${children}</div>`;
}

/**
 * @param {FieldInputProps & { toPatch: (draft: any) => FieldPatch, initialDraft: any, inputType?: string, inputMode?: string, placeholder?: string, multiline?: boolean }} props
 */
function TextLikeInput({ field, onCommit, toPatch, initialDraft, inputType = 'text', inputMode, placeholder, multiline = false }) {
  const [draft, setDraft] = useState(initialDraft);
  const committedRef = useRef(false);
  const commit = (/** @type {EditNavigation} */ navigation) => {
    if (committedRef.current) return;
    committedRef.current = true;
    onCommit(toPatch(draft), navigation);
  };
  /** @param {KeyboardEvent} event */
  const handleKeyDown = (event) => {
    if (multiline && event.key === 'Enter' && event.shiftKey) return;
    const action = getKeyAction(event);
    if (!action) return;
    event.preventDefault();
    event.stopPropagation();
    commit(action === 'commit' ? null : action);
  };
  const inputProps = {
    class: 'field-input-control',
    value: draft ?? '',
    placeholder: placeholder ?? translateMetadataLabel(field.label),
    onInput: (event) => setDraft(event.currentTarget.value),
    onKeyDown: handleKeyDown,
    ref: (element) => {
      if (element && !element.dataset.focused) {
        element.dataset.focused = 'true';
        element.focus();
        if (typeof element.setSelectionRange === 'function' && inputType === 'text') {
          const length = element.value.length;
          element.setSelectionRange(length, length);
        }
      }
    },
  };
  return html`<${EditorSurface} onOutside=${() => commit(null)}>
    ${multiline
      ? html`<textarea rows="3" ...${inputProps}></textarea>`
      : html`<input type=${inputType} inputmode=${inputMode} ...${inputProps} />`}
  <//>`;
}

/**
 * @param {FieldInputProps & { subFields: { name: string, placeholder: string }[], initialDraft: Record<string, any>,
 *   toPatch: (draft: Record<string, any>) => FieldPatch }} props
 */
function MultiTextInput({ onCommit, subFields, initialDraft, toPatch }) {
  const [draft, setDraft] = useState(initialDraft);
  const committedRef = useRef(false);
  const commit = (/** @type {EditNavigation} */ navigation) => {
    if (committedRef.current) return;
    committedRef.current = true;
    onCommit(toPatch(draft), navigation);
  };
  return html`<${EditorSurface} onOutside=${() => commit(null)} wide=${subFields.length > 2}>
    <div class="field-input-multi" data-columns=${subFields.length === 2 ? '2' : '1'}>
      ${subFields.map(
        (subField, index) => html`<input
          key=${subField.name}
          class="field-input-control"
          value=${draft[subField.name] ?? ''}
          placeholder=${subField.placeholder}
          ref=${(element) => {
            if (element && index === 0 && !element.dataset.focused) {
              element.dataset.focused = 'true';
              element.focus();
            }
          }}
          onInput=${(event) => setDraft({ ...draft, [subField.name]: event.currentTarget.value })}
          onKeyDown=${(event) => {
            // Tab entre os sub-campos; no último, confirma e anda para a próxima célula.
            if (event.key === 'Tab' && !event.shiftKey && index < subFields.length - 1) return;
            if (event.key === 'Tab' && event.shiftKey && index > 0) return;
            const action = getKeyAction(event);
            if (!action) return;
            event.preventDefault();
            event.stopPropagation();
            commit(action === 'commit' ? null : action);
          }}
        />`,
      )}
    </div>
  <//>`;
}

/**
 * Endereço com CEP primeiro: CEP completo consulta o Worker e preenche rua, bairro (no complemento), cidade e estado;
 * país padrão Brasil (contrato 08 de navegação, seção 5). Teclas e clique fora como os outros editores.
 * @param {FieldInputProps} props
 */
function AddressInput({ field, record, onCommit, initialCharacter }) {
  const value = record?.[field.name] ?? {};
  const [draft, setDraft] = useState(() => ({
    ...value,
    addressPostcode: initialCharacter && /\d/.test(initialCharacter) ? initialCharacter : value.addressPostcode ?? '',
    addressCountry: toCountryLabel(value.addressCountry || DEFAULT_ADDRESS_COUNTRY),
  }));
  const lookup = usePostcodeLookup(draft, setDraft, { countryAsLabel: true, initialDigits: readPostcodeDigits(value.addressPostcode) });
  const draftRef = lookup.draftRef;
  const committedRef = useRef(false);
  const commit = (/** @type {EditNavigation} */ navigation) => {
    if (committedRef.current) return;
    committedRef.current = true;
    const current = draftRef.current;
    const addressCountry = fromCountryLabel(current.addressCountry);
    onCommit(simplePatch(field, {
      ...value, ...current, addressCountry, addressPostcode: formatPostcodeForStorage(current.addressPostcode, addressCountry),
    }), navigation);
  };
  const subFields = [
    { name: 'addressPostcode', placeholder: t('fieldInput.addressPostcode'), inputMode: 'numeric' },
    { name: 'addressStreet1', placeholder: t('fieldInput.addressStreet1') },
    { name: 'addressStreet2', placeholder: t('navigationMobile.address.neighborhoodHint') },
    { name: 'addressCity', placeholder: t('fieldInput.addressCity') },
    { name: 'addressState', placeholder: t('fieldInput.addressState') },
    { name: 'addressCountry', placeholder: t('fieldInput.addressCountry') },
  ];
  const statusText = lookup.statusText;
  return html`<${EditorSurface} onOutside=${() => commit(null)} wide=${true}>
    <div class="field-input-multi" data-columns="1">
      ${subFields.map((subField, index) => html`<input
        key=${subField.name}
        class="field-input-control"
        value=${draft[subField.name] ?? ''}
        placeholder=${subField.placeholder}
        inputmode=${subField.inputMode}
        autocomplete=${subField.name === 'addressPostcode' ? 'postal-code' : undefined}
        aria-label=${subField.placeholder}
        ref=${(/** @type {HTMLInputElement | null} */ element) => {
          if (element && index === 0 && !element.dataset.focused) {
            element.dataset.focused = 'true';
            element.focus();
          }
        }}
        onInput=${(/** @type {any} */ event) => {
          if (subField.name === 'addressPostcode') {
            lookup.changePostcode(event.currentTarget.value);
            return;
          }
          const next = { ...draftRef.current, [subField.name]: event.currentTarget.value };
          draftRef.current = next;
          setDraft(next);
        }}
        onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
          if (event.key === 'Tab' && !event.shiftKey && index < subFields.length - 1) return;
          if (event.key === 'Tab' && event.shiftKey && index > 0) return;
          const action = getKeyAction(event);
          if (!action) return;
          event.preventDefault();
          event.stopPropagation();
          commit(action === 'commit' ? null : action);
        }}
      />`)}
      ${statusText
        ? html`<p class="address-postcode-status" role="status" data-variant=${lookup.statusVariant}>${statusText}</p>`
        : null}
    </div>
  <//>`;
}

/**
 * Só o CEP (linha "CEP" da família e coluna CEP, contrato 07, seção 9.9.2): consulta como o editor de endereço e grava
 * o CEP com o lugar dele (planPostcodeEdit: cidade, UF e bairro seguem o CEP, inclusive o bairro que veio do CEP
 * gravado; a rua com número fica). A prévia mostra o que vai ser gravado e o que ficou e pode ser de outro lugar.
 * Enter, Tab e clique fora confirmam; CEP pela metade não é gravado (Enter e Tab mantêm o editor com o aviso, clique
 * fora e Escape voltam ao gravado). Gravado com a consulta em andamento, o CEP vai na hora e o resto é completado
 * quando ela volta, se o CEP do registro ainda for o mesmo. CEP não encontrado ou serviço fora do ar: grava só o CEP.
 * @param {FieldInputProps} props
 */
function PostcodeInput({ field, record, onCommit, onCancel, initialCharacter }) {
  const value = record?.[field.name] ?? {};
  const savedDigits = postcodeDigitsOf(value);
  const [draft, setDraft] = useState(() => ({
    ...value,
    addressPostcode: initialCharacter && /\d/.test(initialCharacter) ? initialCharacter : readSubFieldValue(record, field),
  }));
  const lookup = usePostcodeLookup(draft, setDraft, { initialDigits: savedDigits });
  // consulta do CEP gravado: diz que rua e bairro vieram dele (e saem com ele); o navegador guarda a resposta por um dia
  const [savedLookup] = useState(() => (savedDigits ? lookupPostcode(savedDigits).catch(() => null) : Promise.resolve(null)));
  const [savedAddress, setSavedAddress] = useState(/** @type {PostcodeAddress | null} */ (null));
  useEffect(() => {
    let isActive = true;
    savedLookup.then((address) => {
      if (isActive) setSavedAddress(address);
    });
    return () => {
      isActive = false;
    };
  }, []);
  const [isIncompleteShown, setIsIncompleteShown] = useState(false);
  const [isInList, setIsInList] = useState(/** @type {boolean | null} */ (null));
  const committedRef = useRef(false);
  const text = String(draft.addressPostcode ?? '').trim();
  const action = postcodeInputAction(text, value);
  const typedDigits = readPostcodeDigits(text);
  const plan = action === 'SAVE' && lookup.status === 'filled' && lookup.lastAddress && readPostcodeDigits(lookup.lastAddress.postcode) === typedDigits
    ? planPostcodeEdit(value, lookup.lastAddress, savedAddress)
    : null;

  /** @param {EditNavigation} navigation @param {boolean} isConfirmKey Enter ou Tab */
  const commit = (navigation, isConfirmKey) => {
    if (committedRef.current) return;
    if (action === 'INCOMPLETE') {
      if (isConfirmKey) {
        setIsIncompleteShown(true);
        return;
      }
      committedRef.current = true;
      onCancel();
      return;
    }
    committedRef.current = true;
    if (action === 'CLEAR') {
      onCommit(buildSubFieldClearPatch(field, record), navigation);
      return;
    }
    if (action === 'UNCHANGED') {
      onCommit(null, navigation);
      return;
    }
    if (plan) {
      onCommit(buildAddressPartsPatch(field, value, plan.changes), navigation);
      return;
    }
    onCommit(buildSubFieldPatch(field, record, text), navigation);
    const pending = lookup.pendingLookup();
    if (pending) completeAfterLookup(field, record?.id, pending, savedLookup);
  };
  const fieldLabel = translateMetadataLabel(field.sourceLabel ?? field.label);
  const keptLines = plan ? [
    plan.keptStreet ? t('familyPostcode.editor.streetKept', { street: plan.keptStreet }) : null,
    plan.keptNeighborhood ? t('familyPostcode.editor.neighborhoodKept', { neighborhood: plan.keptNeighborhood }) : null,
  ].filter(Boolean) : [];
  const preview = plan ? formatAddressPreview(plan.next) : '';
  // com a prévia, o "preenchido" da consulta sairia antes de gravar: a prévia já diz o que vai acontecer
  const statusText = action === 'INCOMPLETE' || (plan && lookup.status === 'filled') ? null
    : lookup.status === 'notFound' ? t('familyPostcode.editor.notFound') : lookup.statusText;
  const openHousehold = (/** @type {MouseEvent} */ event) => {
    const objectMetadata = objectsState.value.find((candidate) => candidate.id === field.objectMetadataId);
    const source = /** @type {Element} */ (event.currentTarget);
    commit(null, false);
    if (objectMetadata && record?.id) openRecord({ objectNameSingular: objectMetadata.nameSingular, recordId: record.id, source });
  };
  return html`<${EditorSurface} onOutside=${() => commit(null, false)} wide=${true}>
    <div
      class="field-input-multi postcode-input"
      data-columns="1"
      ref=${(/** @type {HTMLDivElement | null} */ element) => {
        if (element && isInList === null) setIsInList(Boolean(element.closest('.record-table, .board-card-field')));
      }}
    >
      <input
        class="field-input-control"
        value=${draft.addressPostcode ?? ''}
        placeholder=${t('familyPostcode.editor.placeholder')}
        aria-label=${translateMetadataLabel(field.label)}
        aria-invalid=${action === 'INCOMPLETE' ? 'true' : undefined}
        inputmode="numeric"
        autocomplete="postal-code"
        maxlength="9"
        ref=${(/** @type {HTMLInputElement | null} */ element) => {
          if (element && !element.dataset.focused) {
            element.dataset.focused = 'true';
            element.focus();
            const length = element.value.length;
            element.setSelectionRange(length, length);
          }
        }}
        onInput=${(/** @type {any} */ event) => {
          setIsIncompleteShown(false);
          lookup.changePostcode(event.currentTarget.value);
        }}
        onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
          const keyAction = getKeyAction(event);
          if (!keyAction) return;
          event.preventDefault();
          event.stopPropagation();
          commit(keyAction === 'commit' ? null : keyAction, keyAction !== 'commit');
        }}
      />
      ${action === 'INCOMPLETE'
        ? html`<p class="address-postcode-status" role="alert" data-variant="error" data-attempted=${isIncompleteShown ? 'true' : undefined}>${t('familyPostcode.form.incomplete')}</p>`
        : null}
      ${statusText
        ? html`<p class="address-postcode-status" role="status" data-variant=${lookup.status === 'notFound' ? 'error' : lookup.statusVariant}>${statusText}</p>`
        : null}
      ${preview ? html`<p class="postcode-input-preview">${t('familyPostcode.editor.preview', { address: preview })}</p>` : null}
      ${keptLines.length > 0
        ? html`<div class="postcode-input-preview" data-tone="warning">
            ${keptLines.map((line) => html`<p class="postcode-input-kept">${line}</p>`)}
            ${isInList
              ? html`<button type="button" class="postcode-input-open" onClick=${openHousehold}>${t('familyPostcode.editor.openRecord')}</button>`
              : html`<p class="postcode-input-kept">${t('familyPostcode.editor.checkBelow', { field: fieldLabel })}</p>`}
          </div>`
        : null}
    </div>
  <//>`;
}

/**
 * CEP gravado antes da consulta voltar: completa o endereço do registro com o lugar do CEP (planPostcodeEdit, a mesma
 * regra do editor), só se o CEP dele ainda for o consultado (a pessoa pode ter trocado de novo nesse meio tempo). A
 * API recebe só as partes que a consulta mudou; o merge do motor completa o resto com o valor do banco.
 * @param {FieldMetadata} field @param {string | undefined} recordId
 * @param {Promise<PostcodeAddress | null>} pending
 * @param {Promise<PostcodeAddress | null>} savedLookup consulta do CEP que estava gravado antes
 */
function completeAfterLookup(field, recordId, pending, savedLookup) {
  const objectMetadata = objectsState.value.find((candidate) => candidate.id === field.objectMetadataId);
  if (!objectMetadata || !recordId) return;
  Promise.all([pending, savedLookup]).then(([address, savedAddress]) => {
    if (!address) return;
    const current = readLoadedRecord(objectMetadata.nameSingular, recordId)?.[field.name];
    if (!current || postcodeDigitsOf(current) !== readPostcodeDigits(address.postcode)) return;
    const patch = buildAddressPartsPatch(field, current, planPostcodeEdit(current, address, savedAddress).changes);
    if (!patch) return;
    updateRecordOptimistically(objectMetadata, recordId, patch.apiPatch, patch.localPatch).catch(() => {});
  });
}

/**
 * Outra parte do endereço (coluna Bairro, Cidade...): uma linha que grava o endereço inteiro com a parte trocada.
 * @param {FieldInputProps} props
 */
function SubFieldTextInput(props) {
  const { field, record, initialCharacter } = props;
  return html`<${TextLikeInput}
    ...${props}
    initialDraft=${initialCharacter ?? readSubFieldValue(record, field)}
    toPatch=${(/** @type {string} */ draft) => buildSubFieldPatch(field, record, draft)}
  />`;
}

/**
 * País e número com máscara; confirma como os outros editores (Enter, Tab, Escape ou clique fora).
 * @param {FieldInputProps} props
 */
function PhoneInput({ field, record, onCommit, initialCharacter }) {
  const value = record?.[field.name];
  // sem número nem país gravados, começa no país padrão do campo (settings do editor de campos; Brasil sem escolha)
  const country = value?.primaryPhoneNumber || value?.primaryPhoneCountryCode || value?.primaryPhoneCallingCode
    ? getPhoneCountry(value)
    : findPhoneCountry(getDefaultPhoneCountryCode(field));
  const [draft, setDraft] = useState({
    countryCode: country.countryCode,
    digits: initialCharacter !== undefined && initialCharacter !== null
      ? toPhoneDigits(initialCharacter, country.countryCode)
      : String(value?.primaryPhoneNumber ?? '').replace(/\D/g, ''),
  });
  const committedRef = useRef(false);
  const commit = (/** @type {EditNavigation} */ navigation) => {
    if (committedRef.current) return;
    committedRef.current = true;
    onCommit(simplePatch(field, toPhonesValue({ ...getEmptyFieldValue(field), ...value }, draft.countryCode, draft.digits)), navigation);
  };
  return html`<${EditorSurface} onOutside=${() => commit(null)} wide=${true}>
    <${PhoneNumberControl}
      draft=${draft}
      onChange=${setDraft}
      autoFocus=${true}
      variant="inline"
      onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
        const action = getKeyAction(event);
        if (!action) return;
        event.preventDefault();
        event.stopPropagation();
        commit(action === 'commit' ? null : action);
      }}
    />
  <//>`;
}

/**
 * @param {FieldInputProps} props
 */
function SelectInput({ field, record, onCommit, onCancel, initialCharacter, emptyOptionLabel }) {
  const [searchText, setSearchText] = useState(initialCharacter ?? '');
  const currentValue = record?.[field.name] ?? null;
  // etapa do card com papel restrito: só Ver e Pode passar (contrato de equipe, seção 3.2)
  const options = scopeStageOptions(field, getSortedOptions(field), 'WRITE', currentValue).filter((option) =>
    normalizeSearchText(translateMetadataLabel(option.label)).includes(normalizeSearchText(searchText)),
  );
  const items = [...(field.isNullable !== false && !searchText ? [null] : []), ...options];
  const choose = (/** @type {any} */ option) => onCommit(simplePatch(field, option ? option.value : null), null);
  const { focusedIndex, setFocusedIndex, handleKeyDown } = useKeyboardSelection(items.length, (index) => choose(items[index]));
  return html`<${EditorSurface} onOutside=${onCancel} className="field-input-dropdown">
    <${DropdownMenuSearchInput}
      value=${searchText}
      placeholder=${t('common.search')}
      onChange=${(value) => {
        setSearchText(value);
        setFocusedIndex(0);
      }}
      onKeyDown=${(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onCancel();
          return;
        }
        handleKeyDown(event);
      }}
    />
    <${DropdownMenuSeparator} />
    <${DropdownMenuItems} hasMaxHeight=${true}>
      ${items.length === 0 ? html`<div class="menu-item-empty">${t('common.noOptionFound')}</div>` : null}
      ${items.map((option, index) =>
        option === null
          ? html`<${MenuItem}
              key="empty"
              text=${emptyOptionLabel ?? t('fieldInput.noValue', { field: translateMetadataLabel(field.label) })}
              icon="IconCircleOff"
              accent="placeholder"
              selected=${currentValue === null}
              focused=${focusedIndex === index}
              onClick=${() => choose(null)}
            />`
          : html`<${MenuItem}
              key=${option.value}
              text=${html`<${Tag} color=${option.color} text=${translateMetadataLabel(option.label)} />`}
              selected=${option.value === currentValue}
              focused=${focusedIndex === index}
              rightComponent=${option.isHandoff ? html`<span class="stage-handoff-mark" title=${t('stageTransition.handoffOption')}><${Icon} name="IconLogout" size=${14} /></span>` : undefined}
              onClick=${() => choose(option)}
            />`,
      )}
    <//>
  <//>`;
}

/**
 * @param {FieldInputProps} props
 */
function MultiSelectInput({ field, record, onCommit }) {
  const [selectedValues, setSelectedValues] = useState(/** @type {string[]} */ (record?.[field.name] ?? []));
  const [searchText, setSearchText] = useState('');
  const options = getSortedOptions(field).filter((option) =>
    normalizeSearchText(translateMetadataLabel(option.label)).includes(normalizeSearchText(searchText)),
  );
  const toggle = (/** @type {string} */ value) =>
    setSelectedValues((current) => (current.includes(value) ? current.filter((item) => item !== value) : [...current, value]));
  const commit = () => onCommit(simplePatch(field, selectedValues), null);
  const { focusedIndex, setFocusedIndex, handleKeyDown } = useKeyboardSelection(options.length, (index) => toggle(options[index].value));
  return html`<${EditorSurface} onOutside=${commit} className="field-input-dropdown">
    <${DropdownMenuSearchInput}
      value=${searchText}
      placeholder=${t('common.search')}
      onChange=${(value) => {
        setSearchText(value);
        setFocusedIndex(0);
      }}
      onKeyDown=${(event) => {
        if (event.key === 'Escape' || (event.key === 'Enter' && (event.metaKey || event.ctrlKey))) {
          event.preventDefault();
          event.stopPropagation();
          commit();
          return;
        }
        handleKeyDown(event);
      }}
    />
    <${DropdownMenuSeparator} />
    <${DropdownMenuItems} hasMaxHeight=${true}>
      ${options.length === 0 ? html`<div class="menu-item-empty">${t('common.noOptionFound')}</div>` : null}
      ${options.map(
        (option, index) => html`<${MenuItem}
          key=${option.value}
          multiSelect=${true}
          text=${html`<${Tag} color=${option.color} text=${translateMetadataLabel(option.label)} />`}
          selected=${selectedValues.includes(option.value)}
          focused=${focusedIndex === index}
          onClick=${() => toggle(option.value)}
        />`,
      )}
    <//>
  <//>`;
}

/**
 * @param {FieldInputProps} props
 */
function RelationInput({ field, record, onCommit, onCancel, createPriorityFieldNames = [] }) {
  const targetObjectNameSingular = field.relation?.targetObjectNameSingular ?? '';
  const targetObject = objectsByNameSingular.value.get(targetObjectNameSingular);
  const currentRelated = record?.[field.name];
  const currentId = record?.[getRelationIdFieldName(field)] ?? currentRelated?.id ?? null;
  const preset = getRelationPreset(field);
  return html`<${EditorSurface} onOutside=${onCancel} className="field-input-dropdown">
    <${RecordPickerList}
      objectNameSingular=${targetObjectNameSingular}
      objectLabel=${preset?.objectLabel}
      selectedIds=${currentId ? [currentId] : []}
      preselectedRecords=${currentId && currentRelated ? [{ id: currentId, label: getRecordLabel(targetObject, currentRelated) }] : []}
      onCreateNew=${(/** @type {string} */ searchText) => {
        // o editor fecha; o registro criado no painel lateral volta como o valor do campo
        onCancel();
        openCreateRecordPanel({
          objectNameSingular: targetObjectNameSingular,
          prefill: { ...(preset?.prefill ?? {}), ...(targetObject ? buildNestedPrefill(targetObject, searchText, '') : {}) },
          priorityFieldNames: createPriorityFieldNames,
          objectLabel: preset?.objectLabel,
          onCreated: (/** @type {any} */ created) => {
            const idFieldName = getRelationIdFieldName(field);
            onCommit(
              {
                apiPatch: { [idFieldName]: created.id },
                localPatch: { [idFieldName]: created.id, [field.name]: created },
              },
              null,
            );
          },
        });
      }}
      onSelect=${(picked) => {
        const idFieldName = getRelationIdFieldName(field);
        if (!picked) {
          onCommit({ apiPatch: { [idFieldName]: null }, localPatch: { [idFieldName]: null, [field.name]: null } }, null);
          return;
        }
        onCommit(
          {
            apiPatch: { [idFieldName]: picked.id },
            localPatch: { [idFieldName]: picked.id, [field.name]: buildRelatedRecord(targetObject, picked) },
          },
          null,
        );
      }}
    />
  <//>`;
}

/**
 * Monta o objeto de relação local (id + campo identificador) para exibição otimista.
 * @param {import('../../lib/field-metadata.js').ObjectMetadata | undefined} targetObject
 * @param {{ id: string, label: string }} picked
 */
export function buildRelatedRecord(targetObject, picked) {
  const labelField = targetObject ? getLabelIdentifierField(targetObject) : undefined;
  if (!labelField) return { id: picked.id, name: picked.label };
  if (labelField.type === FIELD_TYPE.FULL_NAME) {
    const [firstName, ...rest] = picked.label.split(' ');
    return { id: picked.id, [labelField.name]: { firstName, lastName: rest.join(' ') } };
  }
  return { id: picked.id, [labelField.name]: picked.label };
}

/**
 * @param {FieldInputProps} props
 */
export function FieldInput(props) {
  const { field, record, initialCharacter } = props;
  const value = record?.[field.name];
  const startText = (/** @type {string} */ text) => (initialCharacter ? initialCharacter : text);

  // parte do endereço projetada (linha "CEP" da família, colunas CEP, Bairro, Cidade)
  if (isPostcodeProjection(field)) return html`<${PostcodeInput} ...${props} />`;
  if (isSubFieldProjection(field)) return html`<${SubFieldTextInput} ...${props} />`;

  if (isManyToOneRelation(field)) return html`<${RelationInput} ...${props} />`;

  switch (field.type) {
    case FIELD_TYPE.TEXT:
      return html`<${TextLikeInput}
        ...${props}
        initialDraft=${startText(value ?? '')}
        toPatch=${(draft) => simplePatch(field, draft.trim() === '' ? null : draft)}
      />`;
    case FIELD_TYPE.RICH_TEXT:
      return html`<${TextLikeInput}
        ...${props}
        multiline=${true}
        initialDraft=${startText(typeof value === 'object' && value ? value.markdown ?? '' : value ?? '')}
        toPatch=${(draft) => simplePatch(field, { markdown: draft, blocknote: null })}
      />`;
    case FIELD_TYPE.NUMBER:
    case FIELD_TYPE.NUMERIC:
      return html`<${TextLikeInput}
        ...${props}
        inputMode="decimal"
        initialDraft=${startText(value === null || value === undefined ? '' : String(value))}
        toPatch=${(draft) => {
          const normalized = String(draft).replace(/\s/g, '').replace(',', '.');
          if (normalized === '') return simplePatch(field, null);
          const numericValue = Number(normalized);
          if (Number.isNaN(numericValue)) return null;
          return simplePatch(field, field.settings?.dataType === 'int' ? Math.round(numericValue) : numericValue);
        }}
      />`;
    case FIELD_TYPE.UUID:
      return html`<${TextLikeInput}
        ...${props}
        initialDraft=${startText(value ?? '')}
        toPatch=${(draft) => {
          const text = String(draft).trim();
          if (text === '') return simplePatch(field, null);
          return isUuid(text) ? simplePatch(field, text.toLowerCase()) : null;
        }}
      />`;
    case FIELD_TYPE.CURRENCY:
      return html`<${TextLikeInput}
        ...${props}
        inputMode="decimal"
        placeholder=${t('fieldInput.amountPlaceholder')}
        initialDraft=${startText(fromAmountMicros(value?.amountMicros))}
        toPatch=${(draft) => {
          const normalized = String(draft).replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
          const currencyCode = value?.currencyCode || getDefaultCurrencyCode(field);
          if (normalized === '') return simplePatch(field, { amountMicros: null, currencyCode });
          const numericValue = Number(normalized);
          return Number.isNaN(numericValue) ? null : simplePatch(field, { amountMicros: toAmountMicros(numericValue), currencyCode });
        }}
      />`;
    case FIELD_TYPE.DATE:
      return html`<${TextLikeInput}
        ...${props}
        inputType="date"
        initialDraft=${value ? String(value).slice(0, 10) : ''}
        toPatch=${(draft) => simplePatch(field, draft || null)}
      />`;
    case FIELD_TYPE.DATE_TIME:
      return html`<${TextLikeInput}
        ...${props}
        inputType="datetime-local"
        initialDraft=${value ? toLocalDateTimeInput(value) : ''}
        toPatch=${(draft) => simplePatch(field, draft ? new Date(draft).toISOString() : null)}
      />`;
    case FIELD_TYPE.ARRAY:
      return html`<${ArrayFieldInput} ...${props} />`;
    case FIELD_TYPE.SELECT:
      return html`<${SelectInput} ...${props} />`;
    case FIELD_TYPE.MULTI_SELECT:
      return html`<${MultiSelectInput} ...${props} />`;
    case FIELD_TYPE.FULL_NAME:
      return html`<${MultiTextInput}
        ...${props}
        subFields=${[
          { name: 'firstName', placeholder: t('fieldInput.firstName') },
          { name: 'lastName', placeholder: t('fieldInput.lastName') },
        ]}
        initialDraft=${{ firstName: initialCharacter ?? value?.firstName ?? '', lastName: value?.lastName ?? '' }}
        toPatch=${(draft) => simplePatch(field, { firstName: draft.firstName.trim(), lastName: draft.lastName.trim() })}
      />`;
    case FIELD_TYPE.EMAILS:
      return html`<${TextLikeInput}
        ...${props}
        inputType="email"
        placeholder=${t('fieldInput.email')}
        initialDraft=${startText(value?.primaryEmail ?? '')}
        toPatch=${(draft) =>
          simplePatch(field, { primaryEmail: draft.trim(), additionalEmails: value?.additionalEmails ?? [] })}
      />`;
    case FIELD_TYPE.PHONES:
      return html`<${PhoneInput} ...${props} />`;
    case FIELD_TYPE.LINKS:
      return html`<${TextLikeInput}
        ...${props}
        inputType="url"
        placeholder=${t('fieldInput.url')}
        initialDraft=${startText(value?.primaryLinkUrl ?? '')}
        toPatch=${(draft) =>
          simplePatch(field, {
            primaryLinkUrl: draft.trim(),
            primaryLinkLabel: value?.primaryLinkLabel ?? '',
            secondaryLinks: value?.secondaryLinks ?? [],
          })}
      />`;
    case FIELD_TYPE.ADDRESS:
      return html`<${AddressInput} ...${props} />`;
    default:
      return null;
  }
}

/**
 * @param {string} isoValue
 */
export function toLocalDateTimeInput(isoValue) {
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (/** @type {number} */ number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Patch para campos de clique direto (BOOLEAN alterna, RATING recebe o valor escolhido).
 * @param {FieldMetadata} field
 * @param {Record<string, any>} record
 * @param {number | null} [rating]
 * @returns {FieldPatch}
 */
export function getInputOnlyPatch(field, record, rating) {
  if (field.type === FIELD_TYPE.BOOLEAN) return simplePatch(field, !record?.[field.name]);
  if (field.type === FIELD_TYPE.RATING) return simplePatch(field, rating ? `RATING_${rating}` : null);
  return null;
}
